import React, { createContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Intercom from '@intercom/messenger-js-sdk';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import PromptVersions from './pages/PromptVersions';
import Admin from './pages/Admin';
import TestTaking from './pages/TestTaking';
import StartPage from './pages/StartPage';
import SequenceTransition from './pages/SequenceTransition';
import TestReport from './pages/TestReport';
import SequenceReport from './pages/SequenceReport';
import api, { setupApiInterceptors } from './utils/api';

export const AuthContext = createContext();
console.log("We're live");
function AuthProvider({ children }) {
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = () => {
    setTeacher(null);
    localStorage.removeItem('teacher');
    localStorage.removeItem('token');
  };

  const login = (teacher) => {
    console.log('Login - Setting teacher:', teacher);
    setTeacher(teacher);
    localStorage.setItem('teacher', JSON.stringify(teacher));
  };

  // Validate session on app load
  const validateSession = async () => {
    const token = localStorage.getItem('token');
    const storedTeacher = localStorage.getItem('teacher');
    
    if (!token || !storedTeacher) {
      setLoading(false);
      return;
    }

    try {
      // Try to fetch user data to validate token
      const response = await api.get('/api/auth/validate');
      console.log('Auth validation response:', response.data);
      if (response.data.teacher) {
        console.log('Setting teacher in context:', response.data.teacher);
        setTeacher(response.data.teacher);
        localStorage.setItem('teacher', JSON.stringify(response.data.teacher));
      } else {
        // Invalid response, clear session
        console.log('Invalid auth response, logging out');
        logout();
      }
    } catch (error) {
      console.log('Session validation failed:', error.message);
      // Token is invalid or expired, clear session
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Setup API interceptors with logout and navigate callbacks
    setupApiInterceptors(logout, navigate);
    
    // Validate session on mount
    validateSession();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e0e0e0', 
            borderTop: '4px solid #4fc3f7', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#666', fontSize: '16px' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ teacher, login, logout, validateSession }}>
      {children}
    </AuthContext.Provider>
  );
}

function PrivateRoute({ children }) {
  const { teacher, validateSession } = React.useContext(AuthContext);
  const [validating, setValidating] = React.useState(false);

  React.useEffect(() => {
    // If we have a teacher but suspect the session might be invalid,
    // we can optionally re-validate here
    if (teacher && localStorage.getItem('token')) {
      const lastValidation = localStorage.getItem('lastSessionValidation');
      const now = Date.now();
      
      // Re-validate every 5 minutes
      if (!lastValidation || (now - parseInt(lastValidation)) > 5 * 60 * 1000) {
        setValidating(true);
        validateSession().finally(() => {
          localStorage.setItem('lastSessionValidation', now.toString());
          setValidating(false);
        });
      }
    }
  }, [teacher, validateSession]);

  if (validating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '32px', 
            height: '32px', 
            border: '3px solid #e0e0e0', 
            borderTop: '3px solid #4fc3f7', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            margin: '0 auto 12px'
          }}></div>
          <p style={{ color: '#666', fontSize: '14px' }}>Validating session...</p>
        </div>
      </div>
    );
  }

  return teacher ? children : <Navigate to="/login" replace />;
}

function RootRedirect() {
  const { teacher } = React.useContext(AuthContext);
  return <Navigate to={teacher ? "/dashboard" : "/login"} />;
}

export default function App() {
  // Initialize Intercom when app loads
  useEffect(() => {
    Intercom({
      app_id: 't3fte2y1',
    });
  }, []);

  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
          <Route path="/prompt-versions" element={<PrivateRoute><PromptVersions /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
          <Route path="/test/:linkId/start" element={<StartPage />} />
          <Route path="/test/:linkId" element={<TestTaking />} />
          <Route path="/sequence/:linkId/start" element={<StartPage isSequence={true} />} />
          <Route path="/sequence/:linkId/transition" element={<SequenceTransition />} />
          <Route path="/sequence/:linkId" element={<TestTaking isSequence={true} />} />
          <Route path="/test/:linkId/report" element={<TestReport />} />
          <Route path="/sequence/:linkId/report" element={<PrivateRoute><SequenceReport /></PrivateRoute>} />
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}
